import * as React from "react"

import SimpleModal from "@/shadcn/components/SimpleModal"
import { ChangeSummary, type ChangeTotal } from "./ChangeSummary"
import { billDate, type ChangePreview, money } from "./format"

/**
 * Confirms switching between monthly and annual billing. Figma:
 * S311lGXE6JSyTnNAXHmtR7, section "Billing period · monthly and annual"
 * (30262:5925): to annual 30262:5926, to monthly 30262:6237.
 *
 * Plan limits don't change with the billing term, so unlike the old modal this
 * shows only the term, the two prices and the saving.
 */

type TermChangeDialogProps = {
  toAnnual: boolean
  /** List prices for one month and for one year of the same plan and packs. */
  monthly: number
  annual: number
  savings: number
  savingsPct: number
  /** The plan's next bill date, used until the preview gives one. */
  nextBilling: string
  preview: ChangePreview
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
  onClose: () => void
}

export default function TermChangeDialog({
  toAnnual,
  monthly,
  annual,
  savings,
  savingsPct,
  nextBilling,
  preview,
  busy,
  onConfirm,
  onCancel,
  onClose,
}: TermChangeDialogProps) {
  const ready = preview.status === "ready" ? preview : null
  const date = billDate(ready?.nextBillDate ?? nextBilling)
  const perMonth = `${money(monthly)}/mo`
  const perYear = `${money(annual)}/yr`

  let total: ChangeTotal | null = null
  if (preview.status !== "error") {
    if (toAnnual) {
      total = {
        label: "Due today",
        amount: ready ? money(ready.dueToday) : undefined,
        note: `Prorated for this billing period. Your plan renews at ${money(annual)} on ${date}.`,
      }
    } else if (ready && ready.credit <= 0) {
      total = { label: "Due today", amount: money(ready.dueToday), note: `Your next bill is ${money(monthly)} on ${date}.` }
    } else {
      total = {
        label: "Credit to your account",
        amount: ready ? money(ready.credit) : undefined,
        note: `We'll use it toward your monthly bills, starting ${date}.`,
      }
    }
  }

  return (
    <SimpleModal
      title={toAnnual ? "Switch to annual billing?" : "Switch to monthly billing?"}
      description={
        toAnnual
          ? `Pay once a year and save ${savingsPct}% compared with paying monthly.`
          : `You'll pay ${money(monthly)} a month and lose your ${savingsPct}% annual saving.`
      }
      cancelLabel={toAnnual ? "Stay monthly" : "Stay annual"}
      confirmLabel={
        toAnnual
          ? ready && ready.dueToday > 0
            ? `Switch and pay ${money(ready.dueToday)}`
            : "Switch to annual"
          : "Switch to monthly"
      }
      confirmDisabled={preview.status === "loading"}
      busy={busy}
      busyLabel="Saving..."
      onConfirm={onConfirm}
      onCancel={onCancel}
      onClose={onClose}
    >
      <ChangeSummary
        rows={[
          toAnnual
            ? { label: "Billing", from: "Monthly", to: "Annual" }
            : { label: "Billing", from: "Annual", to: "Monthly" },
          toAnnual ? { label: "Price", from: perMonth, to: perYear } : { label: "Price", from: perYear, to: perMonth },
          ...(toAnnual && savings > 0 ? [{ label: "You save", to: `${money(savings)} a year` }] : []),
        ]}
        total={total}
      />
      {preview.status === "error" ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          {toAnnual
            ? "You'll be charged a prorated amount today, less what's left of this month."
            : "What's left of your annual plan is credited to your account."}
        </p>
      ) : null}
    </SimpleModal>
  )
}
